import { observable, makeObservable, computed, action } from 'mobx';
import MobileDetect from 'mobile-detect';
import { v4 as uuidv4 } from 'uuid';
import { RootStore } from './root';
import {
  PRESETS,
  ViewPreset,
  SocialProviders,
  SocialButtonDesign,
  SwitchLinkDesign,
  LoaderColors,
  DEFAULT_LOGO_HEIGHT,
} from '../utils/presets';
import { applyPartnerOverride, resolvePreset } from '../utils/presetDetection';
import { merge } from 'lodash';
import {
  SEARCH_PARAMS,
  STUDIO_HOME_URL,
  WIX_HOME_URL,
} from '../utils/constants';
import wixLogo from '../assets/images/wixLogo.svg?url';
import wixStudioLogo from '../assets/images/wixStudioLogo.svg?url';

type IDirection = 'horizontal' | 'vertical';

interface IField {
  direction: IDirection;
  width: string;
  height: (size: 'small' | 'high') => string | undefined;
}

const defaultViewPreset = (isStudio = false): ViewPreset => ({
  nameKey: 'presets.names.wix',
  skin: 'standard',
  logo: {
    src: isStudio ? wixStudioLogo : wixLogo,
    position: 'left',
    height: DEFAULT_LOGO_HEIGHT,
    mobile: {
      height: '20px',
    },
    alt: 'Wix',
    url: isStudio ? STUDIO_HOME_URL : WIX_HOME_URL,
  },
  buttonDesign: {
    skin: 'standard',
    fullWidth: false,
  },
  socialProviders: `${SocialProviders.GOOGLE},${SocialProviders.FACEBOOK},${SocialProviders.APPLE}`,
  titleCustomFontSize: {
    authPage: {
      desktop: 'xxLarge',
      mobile: 'xLarge',
    },
    secondaryFlow: {
      desktop: 'large',
      mobile: 'medium',
    },
  },
  signup: {
    flows: [
      {
        id: 'domains',
        title: {
          key: 'signup.flows.domains.title',
          size: 'medium',
        },
      },
      {
        id: 'businessLauncher',
        title: {
          key: 'signup.flows.businessLauncher.title',
          size: 'medium',
        },
      },
    ],
  },
  policies: {
    policyKey: 'policies_section.signup_content',
  },
});

export enum AppViewMode {
  FULL_SCREEN = 'FULL_SCREEN',
  MODAL = 'MODAL',
  COMPONENT = 'COMPONENT',
}

export class DisplayStore {
  private readonly rootStore: RootStore;
  private onResizeListeners: Map<string, (location: Location) => any>;
  public userAget = window.navigator.userAgent;
  public mobileDetect = new MobileDetect(window.navigator.userAgent);
  public screenInnerWidth: number = window.innerWidth;
  public viewMode: AppViewMode;
  public preset: ViewPreset;
  public presetKey: string;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      screenInnerWidth: observable,
      userAget: observable,
      mobileDetect: observable,
      viewMode: observable,
      preset: observable,
      presetKey: observable,
      isMobile: computed,
      showMainHeader: computed,
      dialogContainerClass: computed,
      showModalHeader: computed,
      isEditorX: computed,
      authFormWidth: computed,
      direction: computed,
      socialButtonsWidth: computed,
      buttonHeight: computed,
      buttonFontSize: computed,
      socialButtonHeight: computed,
      socialButtonBorderColor: computed,
      socialButtonBorderWidth: computed,
      socialButtonBorderRadius: computed,
      socialButtonBackgroundColor: computed,
      socialButtonHoverBackgroundColor: computed,
      socialButtonActiveBackgroundColor: computed,
      socialButtonHoverBorderColor: computed,
      socialButtonFocusBackgroundColor: computed,
      socialButtonFocusOutline: computed,
      socialButtonFocusOutlineOffset: computed,
      socialButtonIconTextLayout: computed,
      socialButtonFontSize: computed,
      inputButtonGap: computed,
      inputForgotEmailGap: computed,
      forgotEmailButtonGap: computed,
      secondaryDialogInputButtonGap: computed,
      policiesDividerSpacingTop: computed,
      policiesDividerSpacingBottom: computed,
      policiesDividerCenterVertically: computed,
      titleSectionGap: computed,
      loaderColorsfullPage: computed,
      showCardLoader: computed,
      hasCustomTitleSectionGap: computed,
      isBackButtonAboveDialog: computed,
      showBackButtonInHeaderOnMobile: computed,
      mobileBackButtonMarginTop: computed,
      mobileBackButtonMarginStart: computed,
      mobileBackButtonIconTextGap: computed,
      isDialogCentered: computed,
      dialogMinTopOffset: computed,
      isMobileFullScreen: computed,
      sideBySideForm: computed,
      switchLinkDesign: computed,
      switchLinkColor: computed,
      switchLinkHoverColor: computed,
      switchLinkFocusRingColor: computed,
      switchLinkUnderline: computed,
      switchLinkFontWeight: computed,
      updateScreenSize: action,
      resetToDefaultPreset: action,
    });
    this.setOnResizeListener();
    this.updateScreenSize();
    this.init();
  }

  get isMobile(): boolean {
    return (
      !!(
        this.mobileDetect.mobile() ||
        this.mobileDetect.phone() ||
        this.mobileDetect.tablet()
      ) || this.screenInnerWidth <= 810
    );
  }

  get isIframe(): boolean {
    return window.self !== window.top;
  }

  // We can use computed here because it's relevant for all pages and so will always affect the view
  get showMainHeader(): boolean {
    return this.viewMode === AppViewMode.FULL_SCREEN;
  }

  get showModalHeader(): boolean {
    return !this.isFullScreenMode;
  }

  get authFormWidth(): string {
    if (!this.isMobile) {
      return '774px';
    }
    // For mobileFullScreen, let padding control content width
    if (this.isMobileFullScreen) {
      return '100%';
    }
    return this.isVerticalLayout && this.screenInnerWidth <= 380
      ? '270px'
      : '340px';
  }

  get socialButtonsWidth(): string {
    return this.isMobile ? '100%' : '320px';
  }

  // Only for the actual social button elements
  get socialButtonElementWidth(): string {
    const isNewGoogleSdk = this.rootStore.experiments?.enabled(
      'specs.ident.UseNewGoogleSdkReactLoginApp',
    );
    if (!this.isMobile) {
      return '320px';
    }
    // Mobile with new Google SDK: use fixed pixel widths for consistency with Google button
    if (isNewGoogleSdk) {
      // Vertical layout on normal screens
      if (this.isVerticalLayout && this.screenInnerWidth > 380) {
        return '320px';
      }
      // Narrow screens (≤380px), any layout
      if (this.screenInnerWidth <= 380) {
        return '270px';
      }
      // Horizontal layout on normal screens (>380px)
      return '340px';
    }
    // Mobile with old SDK: responsive width
    return '100%';
  }

  get buttonHeight(): string {
    return this.preset.buttonDesign?.height ?? '40px';
  }

  get buttonFontSize(): string | undefined {
    return this.preset.buttonDesign?.fontSize;
  }

  get resolvedSocialButtonDesign(): SocialButtonDesign | undefined {
    const isNewGoogleSdk = this.rootStore.experiments?.enabled(
      'specs.ident.UseNewGoogleSdkReactLoginApp',
    );
    if (isNewGoogleSdk) {
      return {
        ...this.preset.socialButtonDesign,
        ...this.preset.newGoogleSdkSocialButtonDesign,
      };
    }
    return this.preset.socialButtonDesign;
  }

  get socialButtonHeight(): string | undefined {
    return this.resolvedSocialButtonDesign?.height;
  }

  get socialButtonBorderColor(): string | undefined {
    return this.resolvedSocialButtonDesign?.borderColor;
  }

  get socialButtonBorderWidth(): string | undefined {
    return this.resolvedSocialButtonDesign?.borderWidth;
  }

  get socialButtonBorderRadius(): string | undefined {
    return this.resolvedSocialButtonDesign?.borderRadius;
  }

  get socialButtonBackgroundColor(): string | undefined {
    return this.resolvedSocialButtonDesign?.backgroundColor;
  }

  get socialButtonHoverBackgroundColor(): string | undefined {
    return this.resolvedSocialButtonDesign?.hoverBackgroundColor;
  }

  get socialButtonActiveBackgroundColor(): string | undefined {
    return this.resolvedSocialButtonDesign?.activeBackgroundColor;
  }

  get socialButtonHoverBorderColor(): string | undefined {
    return this.resolvedSocialButtonDesign?.hoverBorderColor;
  }

  get socialButtonFocusBackgroundColor(): string | undefined {
    return this.resolvedSocialButtonDesign?.focusBackgroundColor;
  }

  get socialButtonFocusOutline(): string | undefined {
    return this.resolvedSocialButtonDesign?.focusOutline;
  }

  get socialButtonFocusOutlineOffset(): string | undefined {
    return this.resolvedSocialButtonDesign?.focusOutlineOffset;
  }

  get socialButtonIconTextLayout(): 'centered' | 'space-between' {
    return this.resolvedSocialButtonDesign?.iconTextLayout ?? 'space-between';
  }

  get socialButtonFontSize(): string {
    return this.resolvedSocialButtonDesign?.fontSize ?? '14px';
  }

  get inputButtonGap(): string | undefined {
    return this.preset.formSpacing?.inputButtonGap;
  }

  get inputForgotEmailGap(): string | undefined {
    return (
      this.preset.formSpacing?.inputForgotEmailGap ??
      this.preset.formSpacing?.inputButtonGap
    );
  }

  get forgotEmailButtonGap(): string | undefined {
    return this.preset.formSpacing?.forgotEmailButtonGap;
  }

  // Returns the gap between input and button in secondary dialogs (e.g., SSO enter email)
  // Respects mobile/desktop configuration
  get secondaryDialogInputButtonGap(): string | undefined {
    const config = this.preset.formSpacing?.secondaryDialogInputButtonGap;
    if (!config) {
      return undefined;
    }
    return this.isMobile ? config.mobile : config.desktop;
  }

  get policiesDividerSpacingTop(): string {
    return this.preset.formSpacing?.policiesDividerSpacing?.top ?? '32px';
  }

  get policiesDividerSpacingBottom(): string {
    return this.preset.formSpacing?.policiesDividerSpacing?.bottom ?? '24px';
  }

  get policiesDividerCenterVertically(): boolean {
    return (
      this.preset.formSpacing?.policiesDividerSpacing?.centerVertically ?? false
    );
  }

  // Error toast icon color (SectionHelper handles background and text colors)
  get errorToastIconColor(): string {
    return this.preset.errorToastDesign?.iconColor ?? '#E62214';
  }

  get loaderColorsfullPage(): Required<LoaderColors> | undefined {
    const colors = this.preset.loaderColors?.fullPage;
    if (colors?.fill && colors?.fillLoading) {
      return { fill: colors.fill, fillLoading: colors.fillLoading };
    }
    return undefined;
  }

  get showCardLoader(): boolean {
    return this.preset.showCardLoader === true;
  }

  get titleSectionGap(): string {
    // For mobile fullScreen, use 12px gap (Figma spec)
    if (this.isMobileFullScreen) {
      return '12px';
    }
    return (
      this.preset.formSpacing?.titleSectionGap ??
      (this.isVerticalLayout ? '3px' : '1')
    );
  }

  // Returns true when a custom titleSectionGap is set in the preset (desktop only)
  // Used to determine if margin-bottom should be removed from titles to let gap control spacing
  get hasCustomTitleSectionGap(): boolean {
    return (
      !this.isMobile && this.preset.formSpacing?.titleSectionGap !== undefined
    );
  }

  get isBackButtonAboveDialog(): boolean {
    return this.preset.backButtonPosition === 'aboveDialog';
  }

  get showBackButtonInHeaderOnMobile(): boolean {
    return this.preset.mobileBackButtonConfig?.position === 'header';
  }

  get mobileBackButtonMarginTop(): string | undefined {
    return this.preset.mobileBackButtonConfig?.marginTop;
  }

  get mobileBackButtonMarginStart(): string | undefined {
    return this.preset.mobileBackButtonConfig?.marginStart;
  }

  get mobileBackButtonIconTextGap(): string | undefined {
    return this.preset.mobileBackButtonConfig?.iconTextGap;
  }

  get isDialogCentered(): boolean {
    // Don't use dialog centering for mobile fullScreen mode
    if (this.isMobileFullScreen) {
      return false;
    }
    return this.preset.dialogVerticalPosition === 'centered';
  }

  get dialogMinTopOffset(): string | undefined {
    return this.preset.dialogMinTopOffset;
  }

  get isMobileFullScreen(): boolean {
    return this.isMobile && this.preset.container?.mobile?.fullScreen === true;
  }

  // Switch link design (Sign Up, Log In, Continue with SSO links)
  get switchLinkDesign(): SwitchLinkDesign | undefined {
    return this.preset.switchLinkDesign;
  }

  get switchLinkColor(): string | undefined {
    return this.switchLinkDesign?.color;
  }

  get switchLinkHoverColor(): string | undefined {
    return this.switchLinkDesign?.hoverColor;
  }

  get switchLinkFocusRingColor(): string | undefined {
    return this.switchLinkDesign?.focusRingColor;
  }

  get switchLinkUnderline(): 'none' | 'always' | 'onHover' | undefined {
    return this.switchLinkDesign?.underline;
  }

  get switchLinkFontWeight(): string | undefined {
    return this.switchLinkDesign?.fontWeight;
  }

  get direction(): IDirection {
    return this.isMobile || this.isVerticalLayout ? 'vertical' : 'horizontal';
  }

  get isVerticalLayout(): boolean {
    return this.preset.container?.layout === 'vertical';
  }

  get isWixel(): boolean {
    return this.presetKey === PRESETS.WIXEL;
  }

  get sideBySideForm(): IField {
    return {
      direction: this.direction,
      width: '100%',
      height: (size: 'small' | 'high') => {
        if (this.isMobile) {
          return undefined;
        }
        return size === 'small' ? '230px' : '354px';
      },
    };
  }

  get dialogContainerClass(): string {
    return this.isFullScreenMode
      ? 'fullScreenViewContainer'
      : 'modalViewContainer';
  }

  get isEditorX(): boolean {
    return this.presetKey === PRESETS.EDITORX;
  }

  get isWixEducation(): boolean {
    return (
      this.presetKey === PRESETS.EDUCATION_STUDENT ||
      this.presetKey === PRESETS.EDUCATION_TEACHER
    );
  }

  get isFullScreenMode(): boolean {
    return this.viewMode === AppViewMode.FULL_SCREEN;
  }

  public extractBottomPadding(padding: string): number | undefined {
    if (!padding) {
      return undefined;
    }
    try {
      const currentPadding = padding.split(' ');
      if (currentPadding.length !== 4) {
        return undefined;
      }
      const currentBottomPadding = parseInt(currentPadding[2], 10);
      return currentBottomPadding;
    } catch (e) {
      return undefined;
    }
  }

  public updateScreenSize() {
    if (this.userAget !== window.navigator.userAgent) {
      this.mobileDetect = new MobileDetect(window.navigator.userAgent);
      this.userAget = window.navigator.userAgent;
    }
    this.screenInnerWidth = window.innerWidth;
  }

  public getPresetHeaders() {
    switch (this.presetKey) {
      case PRESETS.WIXEL:
        return { 'x-wix-brand': PRESETS.WIXEL };
      case PRESETS.DAYFUL:
        return { 'x-wix-brand': PRESETS.DAYFUL };
      default:
        if (this.rootStore.isStudio) {
          return { 'x-wix-brand': 'studio' };
        }
        return {};
    }
  }

  registerOnResize(callback: (location: Location) => any) {
    const callbackId = uuidv4();
    this.onResizeListeners.set(callbackId, callback);
    return callbackId;
  }

  unRegisterOnResize(callbackId: string) {
    this.onResizeListeners.delete(callbackId);
  }

  private setOnResizeListener() {
    if (!this.onResizeListeners) {
      this.onResizeListeners = new Map();
      this.registerOnResize(this.updateScreenSize.bind(this));
    }
    window.addEventListener('resize', () =>
      this.onResizeListeners.forEach((value) => {
        value(location);
      }),
    );
  }

  private init() {
    this.initViewMode();
    this.initPreset();
    this.initFlow();
    this.initCssVariables();
  }

  private initViewMode() {
    const viewMode: AppViewMode = this.rootStore.navigationStore.getQueryParam(
      'viewMode',
    ) as AppViewMode;
    const isIframe = window.self !== window.top;
    // The default screen view mode for iframe is 'MODAL'
    const defaultViewMode = isIframe
      ? AppViewMode.MODAL
      : AppViewMode.FULL_SCREEN;
    this.viewMode = viewMode ? viewMode : defaultViewMode;
  }

  private initFlow() {
    const flow = this.rootStore.navigationStore.getQueryParam('flow');
    const loginFlow = this.preset.login?.flows?.find(
      (_flow) => _flow.id === flow,
    );
    if (loginFlow && this.preset.login) {
      this.preset.login = Object.assign(this.preset.login, loginFlow);
    }
    const signupFlow = this.preset.signup?.flows?.find(
      (_flow) => _flow.id === flow,
    );
    if (signupFlow && this.preset.signup) {
      this.preset.signup = Object.assign(this.preset.signup, signupFlow);
    }
  }

  private initPreset() {
    const { getQueryParam, baseDomain } = this.rootStore.navigationStore;
    const { userType, isStudio } = this.rootStore;

    const { presetKey, preset } = resolvePreset({
      getQueryParam,
      baseDomain,
      userType,
      origin: window.location.origin,
      pathname: window.location.pathname,
      experiments: this.rootStore.experiments,
    });

    // Store resolved preset key and merge with defaults
    this.presetKey = presetKey || '';
    this.preset = merge(defaultViewPreset(isStudio), preset);
    this.preset = applyPartnerOverride(
      this.preset,
      getQueryParam(SEARCH_PARAMS.PARTNER),
    );
    console.log('[DisplayStore] Final presetKey:', this.presetKey);
  }

  initCssVariables() {
    if (this.preset.cssVariables) {
      const cssVars = Object.entries(this.preset.cssVariables);
      cssVars.forEach(([name, value]) => {
        document.documentElement.style.setProperty(name, value);
      });
    }
    // Apply secondary text color override for SCSS consumption
    // Default to Brand theme's D30 (#595D70) so presets without override keep original styling
    // Presets can set secondaryTextColor to override this
    const secondaryTextColor = this.preset.secondaryTextColor || '#595D70';
    document.documentElement.style.setProperty(
      '--secondary-text-color-override',
      secondaryTextColor,
    );
  }

  public resetToDefaultPreset() {
    // Remove CSS variables set by the current preset
    if (this.preset.cssVariables) {
      Object.keys(this.preset.cssVariables).forEach((name) => {
        document.documentElement.style.removeProperty(name);
      });
    }
    // Reset to default preset
    this.presetKey = '';
    this.preset = defaultViewPreset(this.rootStore.isStudio);
    this.initCssVariables();
  }
}
